import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { DatePipe, DecimalPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';

import { AiEvaluationsService } from '../../../core/admin/ai-evaluations.service';
import { AiEvaluationDetail } from '../../../core/admin/ai-evaluations.types';
import { IconComponent } from '../../../shared/icon/icon';
import { UsdPipe } from '../../../shared/pipes/usd.pipe';
import { DashPipe } from '../../../shared/pipes/dash.pipe';

/** Badge variant for an advisory recommendation chip. */
type RecVariant = 'positive' | 'negative' | 'warning' | 'neutral';

const REC_VARIANT: Record<string, RecVariant> = {
  APPROVE: 'positive',
  DECLINE: 'negative',
  MORE_INFO: 'warning',
};

/** One text block extracted from a Claude message body for display. */
interface PromptBlock {
  kind: 'text' | 'document' | 'image' | 'other';
  /** Visible text (text blocks) or a one-line descriptor (document/image). */
  text: string;
  /** Media type for document/image blocks, e.g. application/pdf. */
  mediaType?: string;
  /** True when the raw bytes were stripped before the body was stored. */
  redacted?: boolean;
}

@Component({
  selector: 'app-ai-evaluation-detail',
  imports: [
    RouterLink,
    DatePipe,
    DecimalPipe,
    MatButtonModule,
    IconComponent,
    UsdPipe,
    DashPipe,
  ],
  templateUrl: './ai-evaluation-detail.html',
  styleUrl: './ai-evaluation-detail.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiEvaluationDetailComponent implements OnInit {
  private readonly service = inject(AiEvaluationsService);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  /** Route param `:id` — the evaluation's short_id. */
  protected readonly shortId = signal('');

  protected readonly evaluation = signal<AiEvaluationDetail | null>(null);
  protected readonly systemPrompt = signal<string>('');
  protected readonly promptBlocks = signal<PromptBlock[]>([]);
  protected readonly responseText = signal<string>('');
  /** Raw request/response JSON, kept for the "show raw" disclosure. */
  protected readonly rawRequest = signal<string>('');
  protected readonly rawResponse = signal<string>('');
  protected readonly bodiesStored = signal(true);

  protected readonly loading = signal(true);
  protected readonly error = signal(false);
  protected readonly showRaw = signal(false);

  // Computed sum of input + output for the "total tokens" line.
  protected readonly totalTokens = computed(() => {
    const e = this.evaluation();
    return e ? e.total_input_tokens + e.output_tokens : 0;
  });

  ngOnInit(): void {
    this.shortId.set(this.route.snapshot.paramMap.get('id') ?? '');
    this.load();
  }

  protected retry(): void {
    this.load();
  }

  protected toggleRaw(): void {
    this.showRaw.update((v) => !v);
  }

  protected recVariant(rec: string): RecVariant {
    return REC_VARIANT[rec] ?? 'neutral';
  }

  /** The structured payload as pretty JSON, or '' when empty. */
  protected payloadJson(): string {
    const e = this.evaluation();
    if (!e || !e.payload || Object.keys(e.payload).length === 0) return '';
    return JSON.stringify(e.payload, null, 2);
  }

  private load(): void {
    this.loading.set(true);
    this.error.set(false);
    this.service
      .detail(this.shortId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.evaluation.set(res.evaluation);
          this.parseRequest(res.request_body);
          this.parseResponse(res.response_body);
          this.bodiesStored.set(res.request_body !== null || res.response_body !== null);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  /** Pull the system prompt + user content blocks out of the Claude request. */
  private parseRequest(body: Record<string, unknown> | null): void {
    if (!body) {
      this.systemPrompt.set('');
      this.promptBlocks.set([]);
      this.rawRequest.set('');
      return;
    }
    this.rawRequest.set(JSON.stringify(body, null, 2));
    this.systemPrompt.set(typeof body['system'] === 'string' ? (body['system'] as string) : '');

    const blocks: PromptBlock[] = [];
    const messages = Array.isArray(body['messages']) ? body['messages'] : [];
    for (const msg of messages as Record<string, unknown>[]) {
      const content = msg?.['content'];
      if (typeof content === 'string') {
        blocks.push({ kind: 'text', text: content });
        continue;
      }
      if (!Array.isArray(content)) continue;
      for (const block of content as Record<string, unknown>[]) {
        blocks.push(this.describeBlock(block));
      }
    }
    this.promptBlocks.set(blocks);
  }

  /** Turn one content block into a display-friendly PromptBlock. */
  private describeBlock(block: Record<string, unknown>): PromptBlock {
    const type = block?.['type'];
    if (type === 'text') {
      return { kind: 'text', text: String(block['text'] ?? '') };
    }
    if (type === 'document' || type === 'image') {
      const source = (block['source'] ?? {}) as Record<string, unknown>;
      const title = (block['title'] as string) || '';
      const mediaType = (source['media_type'] as string) || '';
      const redacted = source['redacted'] === true;
      const label = title || (type === 'document' ? 'Document' : 'Image');
      return { kind: type, text: label, mediaType, redacted };
    }
    return { kind: 'other', text: JSON.stringify(block) };
  }

  /** Pull the assistant's text out of the Claude response message. */
  private parseResponse(body: Record<string, unknown> | null): void {
    if (!body) {
      this.responseText.set('');
      this.rawResponse.set('');
      return;
    }
    this.rawResponse.set(JSON.stringify(body, null, 2));
    const content = body['content'];
    if (!Array.isArray(content)) {
      this.responseText.set('');
      return;
    }
    const parts: string[] = [];
    for (const block of content as Record<string, unknown>[]) {
      if (block?.['type'] === 'text' && typeof block['text'] === 'string') {
        parts.push(block['text'] as string);
      }
    }
    this.responseText.set(parts.join('\n\n'));
  }
}
